import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { groupPoliciesByAction, presentPolicy, redactOpaqueReferences } from '@utils/memoryPresentation';
import {
  useDecidePolicyReview,
  useMemoryAccess,
  usePolicies,
  usePolicyReviews,
} from '@api/hooks/useMemories';
import { isTenantAdminUser, useAuthStore } from '@store/authStore';
import type {
  PolicyReview,
  PolicyReviewDecision,
} from '@schemas/api';

type MemoryTab = 'policies' | 'reviews';

export const MemoriesPage = () => {
  const { data: access, isLoading: accessLoading } = useMemoryAccess();
  const currentUser = useAuthStore((state) => state.currentUser);
  const [activeTab, setActiveTab] = useState<MemoryTab>('policies');
  const [policyStatus, setPolicyStatus] = useState('');

  const canUseTenantScope = access?.can_view_tenant_memories === true;
  const isTenantAdmin = isTenantAdminUser(currentUser);
  const canManagePolicies = canUseTenantScope && isTenantAdmin;
  const visiblePolicyStatus = isTenantAdmin ? policyStatus || undefined : 'active';
  const policies = usePolicies(visiblePolicyStatus, canUseTenantScope && activeTab === 'policies');
  const reviewPolicies = usePolicies(undefined, canManagePolicies && activeTab === 'reviews');
  const reviews = usePolicyReviews('open', canManagePolicies && activeTab === 'reviews');
  const decideReview = useDecidePolicyReview();
  // Shadow policies are internal experiments. They become visible only through
  // an admin review after they reach ready_for_review.
  const displayedPolicies = (policies.data?.items ?? []).filter((policy) => policy.status !== 'shadow');

  const handleReviewDecision = async (review: PolicyReview, decision: PolicyReviewDecision) => {
    const reason = decision === 'reinstate' || decision === 'retire'
      ? window.prompt(`Reason to ${decision} this policy:`)?.trim()
      : '';
    if ((decision === 'reinstate' || decision === 'retire') && !reason) return;
    await decideReview.mutateAsync({
      reviewId: review.review_id,
      decision,
      expectedVersion: review.version,
      reason,
    });
  };

  if (accessLoading) {
    return <LoadingSpinner />;
  }

  if (!canUseTenantScope) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-content-primary">Policies</h1>
          <p className="mt-2 text-content-secondary">
            Approved memory policies are available to the roles selected by your tenant administrator.
          </p>
        </div>
        <section className="rounded-lg border border-border-default bg-surface-base p-6 text-sm text-content-secondary">
          Internal observations are not shown in the workspace. Suggestions that affect your review are explained directly in the relevant document.
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-content-primary">Policies</h1>
          <p className="mt-2 text-content-secondary">
            Review the approved policies that guide AP suggestions. Internal observations remain private to the automation.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border-default pb-3" role="tablist">
        {([
          ['policies', 'Policies'],
          ...(canManagePolicies ? [
            ['reviews', `Review queue${reviews.data?.total ? ` (${reviews.data.total})` : ''}`],
          ] : []),
        ] as Array<[MemoryTab, string]>).map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={`rounded-md px-4 py-2 text-sm font-medium ${activeTab === tab
              ? 'bg-primary-500 text-white'
              : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'policies' && (
        <section className="rounded-lg border border-border-default bg-surface-base">
          <div className="flex flex-col gap-3 border-b border-border-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary-500" />
              <div>
                <h2 className="text-lg font-semibold text-content-primary">{isTenantAdmin ? 'Coding policies' : 'Active coding policies'}</h2>
                <p className="text-sm text-content-secondary">Approved rules that guide GL-account and ERP-item suggestions.</p>
              </div>
            </div>
            {isTenantAdmin && <SearchableSelect
              value={policyStatus}
              onChange={setPolicyStatus}
              options={[
                { id: '', label: 'All statuses' },
                ...['active', 'challenged', 'ready_for_review', 'superseded', 'retired', 'rejected'].map((status) => ({
                  id: status,
                  label: status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
                })),
              ]}
              className="sm:w-48"
              aria-label="Policy status"
              placeholder="All statuses"
              clearable={false}
            />}
          </div>
          <div className="max-h-[650px] space-y-3 overflow-y-auto p-5">
            {policies.isLoading ? <LoadingSpinner /> : policies.isError ? (
              <ErrorPanel message="Could not load policies." />
            ) : !displayedPolicies.length ? <EmptyPanel message="No policies match this filter." /> : (
              groupPoliciesByAction(displayedPolicies).map((group) => {
                const ruleCount = group.rules.length;
                return <article key={group.key} className="rounded-lg border border-border-default p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <span className="rounded-full bg-primary-100 px-2 py-1 text-xs font-medium text-primary-700">{group.category}</span>
                      <h3 className="mt-3 font-semibold text-content-primary">{group.action}</h3>
                      <p className="mt-1 text-sm text-content-secondary">
                        {ruleCount === 1
                          ? 'This outcome has one matching rule.'
                          : `${ruleCount} matching rules lead to this same outcome. Each applies in a different situation.`}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 divide-y divide-border-subtle rounded-lg border border-border-subtle">
                    {group.rules.map(({ policy, presentation }) => (
                      <div key={policy.policy_version_id} className="p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-wide text-content-muted">When</p>
                            <p className="mt-1 text-sm font-medium text-content-primary">
                              {presentation.conditions.length ? presentation.conditions.join(' and ') : 'Any matching line'}
                            </p>
                          </div>
                          {isTenantAdmin && <StatusBadge status={policy.status} />}
                        </div>
                        <p className="mt-2 text-xs text-content-secondary">
                          Scope: {presentation.scope}
                        </p>
                        <p className="mt-2 text-xs text-content-muted">
                          {policy.support_count} supporting decisions · {policy.refutation_count} refuted · {(policy.validation_precision * 100).toFixed(1)}% precision
                        </p>
                      </div>
                    ))}
                  </div>
                </article>;
              })
            )}
          </div>
        </section>
      )}

      {activeTab === 'reviews' && (
        <section className="rounded-lg border border-border-default bg-surface-base">
          <div className="border-b border-border-subtle px-5 py-4">
            <h2 className="text-lg font-semibold text-content-primary">Policy review queue</h2>
            <p className="text-sm text-content-secondary">Counterexamples, new hypotheses, and proposed generalizations.</p>
          </div>
          <div className="max-h-[700px] space-y-4 overflow-y-auto p-5">
            {reviews.isLoading ? <LoadingSpinner /> : reviews.isError ? <ErrorPanel message="Could not load policy reviews." />
              : !reviews.data?.items.length ? <EmptyPanel message="No open policy reviews." />
                : reviews.data.items.map((review) => {
                  const proposedPolicy = review.proposed_policy ? presentPolicy(review.proposed_policy) : null;
                  const affectedPolicy = reviewPolicies.data?.items.find((policy) =>
                    policy.policy_id === review.policy_id || policy.policy_version_id === review.policy_version_id,
                  );
                  const affectedPolicyPresentation = affectedPolicy ? presentPolicy(affectedPolicy) : null;
                  return <article key={review.review_id} className="rounded-lg border border-border-default p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold capitalize text-content-primary">{review.review_type.replace(/_/g, ' ')}</p>
                        <p className="mt-1 text-sm text-content-secondary">{redactOpaqueReferences(review.explanation || 'Review the evidence behind this proposed rule.')}</p>
                      </div>
                      <StatusBadge status={review.status} />
                    </div>
                    {affectedPolicyPresentation && (
                      <div className="mt-3 rounded-md border border-border-subtle p-3 text-sm text-content-secondary">
                        <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">Current behavior</p>
                        <p className="mt-1 font-medium text-content-primary">{affectedPolicyPresentation.title}</p>
                        <p className="mt-1 text-xs text-content-muted">{affectedPolicyPresentation.scope}</p>
                        {affectedPolicyPresentation.conditions.length > 0 && <p className="mt-2">{affectedPolicyPresentation.conditions.join(' · ')}</p>}
                      </div>
                    )}
                    {proposedPolicy && (
                      <div className="mt-3 rounded-md bg-surface-raised p-3 text-sm text-content-secondary">
                        <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">Proposed behavior</p>
                        <p className="mt-1 font-medium text-content-primary">{proposedPolicy.title}</p>
                        <p className="mt-1 text-xs text-content-muted">{proposedPolicy.scope}</p>
                        {proposedPolicy.conditions.length > 0 && <p className="mt-2">{proposedPolicy.conditions.join(' · ')}</p>}
                      </div>
                    )}
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-content-muted sm:grid-cols-4">
                      <div><dt>Confirmed decisions</dt><dd className="font-semibold text-content-primary">{review.supporting_evidence_ids.length}</dd></div>
                      <div><dt>Conflicting decisions</dt><dd className="font-semibold text-content-primary">{review.counterexample_evidence_ids.length}</dd></div>
                      <div><dt>Rules affected</dt><dd className="font-semibold text-content-primary">{review.affected_policy_ids.length}</dd></div>
                      <div><dt>Version</dt><dd className="font-semibold text-content-primary">{review.version}</dd></div>
                    </dl>
                    {canManagePolicies && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {review.review_type === 'counterexample' ? <>
                          <ActionButton label="Reinstate" onClick={() => handleReviewDecision(review, 'reinstate')} disabled={decideReview.isPending} />
                          <ActionButton label="Retire" onClick={() => handleReviewDecision(review, 'retire')} disabled={decideReview.isPending} danger />
                        </> : <>
                          <ActionButton label="Approve" onClick={() => handleReviewDecision(review, 'approve')} disabled={decideReview.isPending} />
                          <ActionButton label="Reject" onClick={() => handleReviewDecision(review, 'reject')} disabled={decideReview.isPending} danger />
                        </>}
                      </div>
                    )}
                  </article>;
                })}
          </div>
        </section>
      )}

    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => (
  <span className="rounded-full bg-surface-raised px-2.5 py-1 text-xs font-medium capitalize text-content-secondary">{status.replace(/_/g, ' ')}</span>
);

const EmptyPanel = ({ message }: { message: string }) => <div className="rounded-lg border border-dashed border-border-default p-6 text-center text-sm text-content-secondary">{message}</div>;
const ErrorPanel = ({ message }: { message: string }) => <div className="rounded-lg border border-red-300 bg-red-50 p-6 text-center text-sm text-red-700">{message}</div>;

const ActionButton = ({ label, onClick, disabled, danger = false }: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean }) => (
  <button type="button" onClick={onClick} disabled={disabled} className={`rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50 ${danger ? 'border border-red-300 text-red-700 hover:bg-red-50' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>{label}</button>
);
